import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import { useAssistancePanel } from '../contexts/AssistancePanelContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePhaseList } from '../hooks/usePhaseList';
import { useViewportSize } from '../hooks/useViewportSize';
import CockpitMarkerStage from './cockpit/CockpitMarkerStage';
import CockpitControlDetail from './cockpit/CockpitControlDetail';
import CockpitChecklistStepsCard from './cockpit/CockpitChecklistStepsCard';
import CockpitStepsBar from './cockpit/CockpitStepsBar';

// Mirrors DashboardLayout's own side-by-side-vs-overlay math so this panel
// knows, independently, whether it's currently rendered as a real desktop
// side panel or as a narrow overlay/bottom sheet.
const ASSISTANCE_WIDTH = 380;
const SIDEBAR_WIDTH = 268;
const MIN_MAIN_WIDTH = 640;
const DESKTOP_BREAKPOINT = 1024;

/**
 * CockpitLocatePanel - renders inside the existing In-Flight Assistance
 * panel slot (DashboardLayout's 380px right column) when a checklist
 * locate icon is tapped on desktop. Shows either a single control or a
 * multi-step "from your checklist" sequence, using the same Cockpit
 * Explorer pieces as the full /cockpit/:zone page.
 */
const CockpitLocatePanel = (props) => {
  const locate = props.locate;
  const explorerData = props.explorerData;
  const { closeLocate } = useAssistancePanel();
  const { isOpen: isSidebarOpen } = useSidebar();
  const { width: viewportWidth } = useViewportSize();
  const { slugByTitle } = usePhaseList();
  const navigate = useNavigate();

  const sidebarSpace = viewportWidth >= DESKTOP_BREAKPOINT && isSidebarOpen ? SIDEBAR_WIDTH : 0;
  const sideBySideMode = viewportWidth - sidebarSpace - ASSISTANCE_WIDTH >= MIN_MAIN_WIDTH;

  const isMulti = locate.type === 'multi';
  const initialControl = isMulti ? locate.steps[0].control : locate.control;
  const [selectedControlId, setSelectedControlId] = useState(initialControl.id);
  const [deselected, setDeselected] = useState(false);

  const allControlsInLocate = isMulti ? locate.steps.map((s) => s.control) : [locate.control];
  const currentStep = isMulti
    ? locate.steps.find((s) => s.control.id === selectedControlId) || locate.steps[0]
    : null;
  const currentControl = isMulti ? currentStep.control : locate.control;
  const currentView = explorerData.zones
    .flatMap((z) => z.views)
    .find((v) => v.id === currentControl.view);
  const viewControls = explorerData.controls.filter((c) => c.view === currentControl.view);
  const currentZoneId = explorerData.zones.find((z) => z.views.some((v) => v.id === currentControl.view))?.id;

  const openFullExplorer = () => {
    navigate(`/cockpit/${currentZoneId}?view=${currentControl.view}&control=${currentControl.id}`, {
      state: isMulti ? { steps: locate.steps, itemText: locate.itemText, phaseTitle: locate.phaseTitle } : undefined,
    });
  };

  return (
    <div className="flex flex-col gap-3" data-testid="cockpit-locate-panel">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={closeLocate}
          className="flex items-center gap-1 text-sm font-barlow-condensed font-semibold cursor-pointer"
          style={{ color: 'var(--gc-text-body)' }}
          data-testid="cockpit-locate-back-btn"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Assistance
        </button>
        <button
          type="button"
          onClick={openFullExplorer}
          className="flex items-center gap-1 text-xs font-plex-mono cursor-pointer"
          style={{ color: 'var(--gc-cyan)' }}
          data-testid="cockpit-locate-open-full"
        >
          Open full Explorer <ExternalLink className="w-3 h-3" />
        </button>
      </div>

      {isMulti && !sideBySideMode && (
        <CockpitStepsBar
          itemText={locate.itemText}
          phaseTitle={locate.phaseTitle}
          steps={locate.steps}
          selectedControlId={deselected ? null : selectedControlId}
          onStepClick={(step) => {
            setDeselected(false);
            setSelectedControlId(step.control.id);
          }}
        />
      )}

      <div style={{ height: '220px', display: 'flex' }}>
        <CockpitMarkerStage
          view={currentView}
          controls={viewControls}
          selectedControlId={deselected ? null : currentControl.id}
          onSelect={(id) => {
            setDeselected(false);
            if (isMulti && allControlsInLocate.some((c) => c.id === id)) setSelectedControlId(id);
          }}
          onDeselect={() => setDeselected(true)}
          highlightIds={isMulti ? allControlsInLocate.map((c) => c.id) : []}
          autoScroll={false}
        />
      </div>

      {isMulti && sideBySideMode && (
        <CockpitChecklistStepsCard
          itemText={locate.itemText}
          phaseTitle={locate.phaseTitle}
          steps={locate.steps}
          selectedControlId={deselected ? null : selectedControlId}
          onStepClick={(step) => {
            setDeselected(false);
            setSelectedControlId(step.control.id);
          }}
        />
      )}

      <CockpitControlDetail control={deselected ? null : currentControl} slugByTitle={slugByTitle} />
    </div>
  );
};

export default CockpitLocatePanel;
